import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { VimeoApiClient, type VimeoVideo, type VimeoTag } from '../services/vimeo-api';
import { debounce } from '../utils/debounce';
import type { ComboboxOption } from './imos-combobox';

// Ensure sub-components are registered
import './imos-video-card';
import './imos-combobox';

type DisplayMode = 'grid' | 'list';

interface MediathekLabels {
  gridView: string;
  listView: string;
  loading: string;
  header: string;
  selectVersion: string;
  selectTags: string;
  search: string;
  resetFilter: string;
  columnName: string;
  columnTags: string;
  columnDuration: string;
  columnDate: string;
  columnDownload: string;
  comboboxSearch: string;
  comboboxMore: string;
  minutes: string;
  perPage: string;
}

const DEFAULT_LABELS: MediathekLabels = {
  gridView: 'Grid view',
  listView: 'List view',
  loading: 'Loading...',
  header: 'Media Library',
  selectVersion: 'Select version',
  selectTags: 'Select tags',
  search: 'Search',
  resetFilter: 'Reset all filters',
  columnName: 'Name',
  columnTags: 'Tags',
  columnDuration: 'Duration',
  columnDate: 'Date',
  columnDownload: 'Download',
  comboboxSearch: 'Search',
  comboboxMore: 'more',
  minutes: 'Minutes',
  perPage: 'per page',
};

@customElement('imos-mediathek')
export class ImosMediathek extends LitElement {
  @property({ type: String, attribute: 'api-base-url' }) apiBaseUrl = '';
  @property({ type: String, attribute: 'folder-map' }) folderMap = 'default';
  @property({ type: String, attribute: 'view-mode' }) viewMode: DisplayMode = 'grid';
  @property({ type: Boolean, attribute: 'hide-view-toggle' }) hideViewToggle = false;
  @property({ type: String, attribute: 'per-page-options' }) perPageOptions = '12,24,36';

  private static readonly DISPLAY_MODE_KEY = 'mediathek-display-mode';
  private static readonly PER_PAGE_KEY = 'mediathek-per-page';

  @state() private displayMode: DisplayMode = 'grid';
  @state() private videos: VimeoVideo[] = [];
  @state() private tags: VimeoTag[] = [];
  @state() private versions: VimeoTag[] = [];
  @state() private loading = false;
  @state() private searchWord = '';
  @state() private selectedTags: string[] = [];
  @state() private selectedVersions: string[] = [];
  @state() private currentPage = 1;
  @state() private totalPages = 1;
  @state() private selectedPerPage = 12;

  private api!: VimeoApiClient;
  private headerHtml = '';
  private labels: MediathekLabels = DEFAULT_LABELS;
  private debouncedFetch = debounce(() => this.fetchVideos(), 500);

  protected createRenderRoot(): this {
    return this;
  }

  override connectedCallback(): void {
    const slottedHeader = this.querySelector('[slot="header"]');
    if (slottedHeader) {
      this.headerHtml = slottedHeader.outerHTML;
      slottedHeader.remove();
    }
    super.connectedCallback();
    this.api = new VimeoApiClient(this.apiBaseUrl);
    this.readLabels();
    this.readTags();
    this.initDisplayMode();
    this.initPerPage();
    const page = new URLSearchParams(window.location.search).get('page');
    if (page) this.currentPage = Math.max(1, parseInt(page, 10) || 1);
    this.fetchVideos();
  }

  private initDisplayMode(): void {
    if (this.hideViewToggle) {
      this.displayMode = this.viewMode;
      return;
    }
    const stored = localStorage.getItem(ImosMediathek.DISPLAY_MODE_KEY) as DisplayMode | null;
    this.displayMode = stored ?? this.viewMode;
  }

  private get perPageChoices(): number[] {
    const choices = this.perPageOptions
      .split(',')
      .map((v) => parseInt(v.trim(), 10))
      .filter((v) => Number.isFinite(v) && v > 0);

    return [...new Set(choices)].sort((a, b) => a - b);
  }

  private initPerPage(): void {
    const choices = this.perPageChoices;
    const stored = parseInt(localStorage.getItem(ImosMediathek.PER_PAGE_KEY) ?? '', 10);
    this.selectedPerPage = choices.includes(stored) ? stored : (choices[0] ?? 12);
  }

  private onPerPageChange(e: CustomEvent<{ values: string[] }>): void {
    const value = parseInt(e.detail.values[0] ?? '', 10);
    if (!this.perPageChoices.includes(value) || value === this.selectedPerPage) return;
    this.selectedPerPage = value;
    localStorage.setItem(ImosMediathek.PER_PAGE_KEY, String(value));
    this.goToPage(1);
  }

  private readLabels(): void {
    const global = (window as any).__MEDIATHEK_LABELS__;
    if (global && typeof global === 'object') {
      this.labels = { ...DEFAULT_LABELS, ...global };
    }
  }

  private readTags(): void {
    const globalTags = (window as any).__MEDIATHEK_TAGS__;
    if (Array.isArray(globalTags)) {
      this.tags = globalTags.map((t: any) => ({
        value: String(t.value),
        title: t.label ?? t.title ?? '',
      }));
    }

    const globalVersions = (window as any).__MEDIATHEK_VERSIONS__;
    if (Array.isArray(globalVersions)) {
      this.versions = globalVersions.map((t: any) => ({
        value: String(t.value),
        title: t.label ?? t.title ?? '',
      }));
    }
  }

  private async fetchVideos(): Promise<void> {
    this.loading = true;
    try {
      const allTags = [...this.selectedTags, ...this.selectedVersions];

      const result = await this.api.getVideos({
        query: this.searchWord || undefined,
        tags: allTags.length > 0 ? allTags : undefined,
        page: this.currentPage,
        per_page: this.selectedPerPage > 0 ? this.selectedPerPage : undefined,
        folderMap: this.folderMap || undefined,
      });
      this.videos = result.videos;
      this.totalPages = result.totalPages;
    } catch (e) {
      console.error('Failed to fetch videos:', e);
    } finally {
      this.loading = false;
    }
  }

  private get isAnyFilterApplied(): boolean {
    return !!(this.searchWord || this.selectedTags.length > 0 || this.selectedVersions.length > 0);
  }

  private toggleDisplayMode(mode?: DisplayMode): void {
    if (this.hideViewToggle) return;
    this.displayMode = mode ?? (this.displayMode === 'grid' ? 'list' : 'grid');
    localStorage.setItem(ImosMediathek.DISPLAY_MODE_KEY, this.displayMode);
  }

  private onSearchInput(e: Event): void {
    this.searchWord = (e.target as HTMLInputElement).value;
    this.currentPage = 1;
    this.debouncedFetch();
  }

  private clearSearch(): void {
    this.searchWord = '';
    this.currentPage = 1;
    this.fetchVideos();
  }

  private onTagsChange(e: CustomEvent<{ values: string[] }>): void {
    this.selectedTags = e.detail.values;
    this.currentPage = 1;
    this.fetchVideos();
  }

  private onVersionChange(e: CustomEvent<{ values: string[] }>): void {
    this.selectedVersions = e.detail.values;
    this.currentPage = 1;
    this.fetchVideos();
  }

  private resetFilter(): void {
    this.searchWord = '';
    this.selectedTags = [];
    this.selectedVersions = [];
    this.currentPage = 1;
    this.querySelectorAll<any>('imos-combobox:not([data-per-page])').forEach((cb) =>
      cb.resetSelection(),
    );
    this.fetchVideos();
  }

  private get comboboxOptions(): ComboboxOption[] {
    return this.tags.map((t) => ({ value: t.value, text: t.title }));
  }

  private get versionOptions(): ComboboxOption[] {
    return this.versions.map((t) => ({ value: t.value, text: t.title }));
  }

  private get perPageComboboxOptions(): ComboboxOption[] {
    return this.perPageChoices.map((n) => ({ value: String(n), text: `${n} ${this.labels.perPage}` }));
  }

  protected render() {
    return html`
      <div>
        ${this.renderHeader()}
        ${this.renderFilterBar()}
        <div data-video-list-top></div>
        ${this.loading
          ? html`<div class="py-m flex flex-col items-center gap-y-2xs text-gray-250">
              <svg class="animate-spin" width="48" height="48" viewBox="0 0 24 24" fill="none">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z"></path>
              </svg>
              <span class="text-sm">${this.labels.loading}</span>
            </div>`
          : this.displayMode === 'grid'
            ? this.renderGrid()
            : this.renderList()}
        ${this.totalPages > 1 ? this.renderPagination() : nothing}
      </div>
    `;
  }

  private renderHeader() {
    return html`
      <div
        class="pb-s mb-xs border-b border-main flex flex-col md:flex-row gap-y-sm justify-between items-baseline"
      >
        ${this.headerHtml
          ? html`${unsafeHTML(this.headerHtml)}`
          : html`<h1 class="h1 mb-0">${this.labels.header}</h1>`}
        ${this.hideViewToggle ? nothing : this.renderDisplayModeToggle()}
      </div>
    `;
  }

  private renderDisplayModeToggle() {
    return html`
      <div class="text-sm flex items-center gap-x-2xs">
        <label class="cursor-pointer" @click=${() => this.toggleDisplayMode('grid')}
          >${this.labels.gridView}</label
        >
        <div
          class="relative inline-block w-10 align-middle select-none transition duration-200 ease-in"
        >
          <input
            type="checkbox"
            name="toggle"
            .checked=${this.displayMode === 'list'}
            @change=${() => this.toggleDisplayMode()}
            class="toggle-checkbox absolute block w-5 h-5 rounded-full bg-white dark:bg-gray-350 border-[3px] border-gray-350 dark:border-gray-100 appearance-none cursor-pointer transition-transform checked:translate-x-5"
          />
          <label
            @click=${() => this.toggleDisplayMode()}
            class="toggle-label block overflow-hidden h-5 rounded-full bg-gray-350 dark:bg-gray-100 cursor-pointer"
          ></label>
        </div>
        <label class="cursor-pointer" @click=${() => this.toggleDisplayMode('list')}
          >${this.labels.listView}</label
        >
      </div>
    `;
  }

  private renderFilterBar() {
    return html`
      <div class="mb-m flex flex-col md:flex-row md:flex-wrap md:justify-between md:items-center gap-x-xs gap-y-2xs">
        ${this.versions.length > 0
          ? html`
              <div>
                <imos-combobox
                  .label=${this.labels.selectVersion}
                  .options=${this.versionOptions}
                  .multiple=${false}
                  .searchable=${false}
                  .searchLabel=${this.labels.comboboxSearch}
                  .moreLabel=${this.labels.comboboxMore}
                  @imos-combobox-change=${this.onVersionChange}
                ></imos-combobox>
              </div>
            `
          : nothing}

        <div>
          <imos-combobox
            .label=${this.labels.selectTags}
            .options=${this.comboboxOptions}
            .searchLabel=${this.labels.comboboxSearch}
            .moreLabel=${this.labels.comboboxMore}
            @imos-combobox-change=${this.onTagsChange}
          ></imos-combobox>
        </div>

        <div>
          <imos-combobox
            data-per-page
            .label=${this.labels.perPage}
            .options=${this.perPageComboboxOptions}
            .values=${[String(this.selectedPerPage)]}
            .multiple=${false}
            .searchable=${false}
            .clearable=${false}
            .triggerClass=${'min-w-[130px]'}
            @imos-combobox-change=${this.onPerPageChange}
          ></imos-combobox>
        </div>

        <div class="md:ml-auto">
          <div
            class="bg-white dark:bg-gray-300 text-main text-xs px-2xs rounded-full flex items-center gap-x-2xs overflow-hidden transition-shadow focus-within:ring-2 focus-within:ring-brand cursor-text"
            @click=${(e: Event) => (e.currentTarget as HTMLElement).querySelector('input')?.focus()}
          >
            <i class="icon--imos-webicon_search"></i>
            <div class="relative">
              <input
                type="text"
                .value=${this.searchWord}
                @input=${this.onSearchInput}
                .placeholder=${this.labels.search}
                class="py-2 w-full md:placeholder-shown:w-l md:w-3xl transition-all bg-inherit border-0 focus:outline-0 focus:ring-0"
              />
              <button
                class="absolute top-1/2 right-0 -translate-y-1/2 bg-inherit ${this.searchWord ? '' : 'invisible'}"
                @click=${() => this.clearSearch()}
              >
                <i class="icon--imos-webicon_x text-gray-250"></i>
              </button>
            </div>
          </div>
        </div>

        ${this.isAnyFilterApplied
          ? html`
              <div class="w-full order-last">
                <button
                  class="text-xs inline-flex items-center gap-x-2xs"
                  @click=${() => this.resetFilter()}
                >
                  <i class="text-brand icon--imos-webicon_x"></i> ${this.labels.resetFilter}
                </button>
              </div>
            `
          : nothing}
      </div>
    `;
  }

  private renderGrid() {
    return html`
      <div
        class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-grid-col-1 gap-y-m"
      >
        ${repeat(
          this.videos,
          (v) => v.videoId,
          (video) => html`<imos-video-card .video=${video} .minutesLabel=${this.labels.minutes}></imos-video-card>`,
        )}
      </div>
    `;
  }

  private openVideo(v: VimeoVideo): void {
    window.dispatchEvent(
      new CustomEvent('imos-video-open', {
        bubbles: true,
        composed: true,
        detail: {
          title: v.title,
          videoId: v.videoId,
          posterUrl: v.poster,
          embedHtml: v.embedHtml,
        },
      }),
    );
  }

  private renderList() {
    return html`
      <!-- < xl: div-based subgrid layout -->
      <div class="xl:hidden">
        ${repeat(
          this.videos,
          (v) => v.videoId,
          (video) => html`
            <div
              class="border-b border-main py-xs cursor-pointer hover:bg-secondary-alt transition-colors grid grid-cols-[1fr_min-content] gap-x-xs gap-y-2xs"
              @click=${() => this.openVideo(video)}
            >
              <div class="grid grid-cols-subgrid col-span-full items-baseline">
                <div class="font-normal text-gray-350 dark:text-gray-100 text-xs">${video.title}</div>
                <div class="flex items-center justify-end gap-x-2 whitespace-nowrap text-xs text-gray-300 dark:text-gray-200">
                  <span>${video.duration} ${this.labels.minutes}</span>
                  <i class="icon--imos-webicon_play text-brand dark:text-white"></i>
                  ${video.downloadUrl
                    ? html`<a
                        href=${video.downloadUrl}
                        download
                        @click=${(e: Event) => e.stopPropagation()}
                        class="text-brand dark:text-white hover:text-brand-dark"
                        ><i class="icon--imos-webicon_downloads"></i
                      ></a>`
                    : nothing}
                </div>
              </div>
              <div class="grid grid-cols-subgrid col-span-full items-center">
                <div>
                  <ul class="badge-list">
                    ${repeat(
                      video.tags,
                      (t) => t.value,
                      (tag) => html`<li><span class="badge">${tag.title}</span></li>`,
                    )}
                  </ul>
                </div>
                <div class="whitespace-nowrap text-xs text-gray-300 dark:text-gray-200 text-right">
                  ${video.date ?? ''}
                </div>
              </div>
            </div>
          `,
        )}
      </div>

      <!-- >= xl: table layout -->
      <table class="hidden xl:table w-full text-xs">
        <thead>
          <tr class="text-gray-300 border-b border-main">
            <th class="text-left font-normal pb-2xs">${this.labels.columnName}</th>
            <th class="text-left font-normal pb-2xs">${this.labels.columnTags}</th>
            <th class="text-left font-normal pb-2xs">${this.labels.columnDate}</th>
            <th class="text-left font-normal pb-2xs">${this.labels.columnDuration}</th>
            <th class="text-left font-normal pb-2xs">${this.labels.columnDownload}</th>
          </tr>
        </thead>
        <tbody>
          ${repeat(
            this.videos,
            (v) => v.videoId,
            (video) => html`
              <tr
                class="group hover:bg-secondary-alt transition-colors border-b border-main cursor-pointer"
                @click=${() => this.openVideo(video)}
              >
                <td class="py-2xs text-xs font-normal text-gray-350 dark:text-gray-100">${video.title}</td>
                <td class="py-2xs">
                  <ul class="badge-list">
                    ${repeat(
                      video.tags,
                      (t) => t.value,
                      (tag) => html`<li><span class="badge group-hover:bg-secondary">${tag.title}</span></li>`,
                    )}
                  </ul>
                </td>
                <td class="py-2xs text-xs text-gray-300 dark:text-gray-200 whitespace-nowrap">${video.date ?? ''}</td>
                <td class="py-2xs">
                  <div class="flex gap-x-2 items-center">
                    <span class="whitespace-nowrap text-xs text-gray-300 dark:text-gray-200">
                      ${video.duration} ${this.labels.minutes}
                    </span>
                    <i class="icon--imos-webicon_play text-brand dark:text-white"></i>
                  </div>
                </td>
                <td class="py-2xs">
                  ${video.downloadUrl
                    ? html`<a
                        href=${video.downloadUrl}
                        download
                        @click=${(e: Event) => e.stopPropagation()}
                        class="text-brand dark:text-white hover:text-brand-dark"
                        ><i class="icon--imos-webicon_downloads"></i
                      ></a>`
                    : nothing}
                </td>
              </tr>
            `,
          )}
        </tbody>
      </table>
    `;
  }

  private goToPage(page: number): void {
    this.currentPage = page;
    const url = new URL(window.location.href);
    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }
    window.history.replaceState(null, '', url);
    this.fetchVideos();
    this.querySelector<HTMLElement>('[data-video-list-top]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private get paginationPages(): (number | '...')[] {
    const total = this.totalPages;
    const current = this.currentPage;

    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: (number | '...')[] = [1];

    if (current <= 4) {
      pages.push(2, 3, 4, '...', total);
    } else if (current >= total - 3) {
      pages.push('...', total - 3, total - 2, total - 1, total);
    } else {
      pages.push('...', current - 1, current, current + 1, '...', total);
    }

    return pages;
  }

  private renderPagination() {
    const btnBase = 'min-w-8 h-8 md:min-w-12 md:h-12 text-sm rounded inline-flex items-center justify-center';
    const btnClass = `${btnBase} bg-white dark:bg-gray-300 hover:bg-secondary`;
    const activeClass = `${btnBase} bg-brand text-white`;
    const disabledClass = `${btnBase} bg-white dark:bg-gray-300 text-gray-250 cursor-default`;

    return html`
      <div class="flex justify-center items-center gap-x-2xs mt-m">
        ${this.currentPage > 1
          ? html`<button class=${btnClass} @click=${() => this.goToPage(this.currentPage - 1)}>
              <i class="icon--imos-webicon_left"></i>
            </button>`
          : html`<span class=${disabledClass}><i class="icon--imos-webicon_left"></i></span>`}
        ${this.paginationPages.map((p) =>
          p === '...'
            ? html`<span class="px-1 py-1 text-sm text-gray-250">&hellip;</span>`
            : html`<button
                class=${p === this.currentPage ? activeClass : btnClass}
                @click=${() => this.goToPage(p)}
              >
                ${p}
              </button>`,
        )}
        ${this.currentPage < this.totalPages
          ? html`<button class=${btnClass} @click=${() => this.goToPage(this.currentPage + 1)}>
              <i class="icon--imos-webicon_right"></i>
            </button>`
          : html`<span class=${disabledClass}><i class="icon--imos-webicon_right"></i></span>`}
      </div>
    `;
  }
}
